"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { BASE_URL } from "../config/apiconfig";

const DiscountTypeContext = createContext(null);

export const DiscountTypeProvider = ({ children }) => {
  const [discountTypes, setDiscountTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const DISCOUNT_ENDPOINT = `${BASE_URL}/discount_type`;

  /* ---------- AUTH HEADERS ---------- */
  const getHeaders = () => {
    const token = localStorage.getItem("token");

    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  };

  /* ---------- NORMALIZE API DATA ---------- */
  const normalizeDiscount = (item) => ({
    id: item.id,
    name: item.name ?? item.type ?? "",
    status: item.status ?? "Active",
  });

  /* ---------- FETCH ---------- */
  const fetchDiscountTypes = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(DISCOUNT_ENDPOINT, {
        headers: getHeaders(),
      });

      if (!res.ok) {
        throw new Error("Failed to fetch discount types");
      }

      const json = await res.json();

      let list = [];

      if (Array.isArray(json)) {
        list = json;
      } else if (Array.isArray(json.data)) {
        list = json.data;
      } else if (json && typeof json === "object" && json.id) {
        list = [json];
      }

      setDiscountTypes(list.map(normalizeDiscount));
    } catch (err) {
      console.error(err);
      setDiscountTypes([]);
      setError("Failed to load discount types");
    } finally {
      setLoading(false);
    }
  }, []);

  /* ---------- ADD ---------- */
 const addDiscountType = async (name) => {
  try {
    const res = await fetch(DISCOUNT_ENDPOINT, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({ name }),
    });

    const json = await res.json();

    // ✅ FIX: extract correct object
    const item =
      json?.data && typeof json.data === "object"
        ? json.data
        : json;

    setDiscountTypes((prev) => [...prev, normalizeDiscount(item)]);
  } catch (err) {
    console.error("Add failed", err);
  }
};


  /* ---------- UPDATE ---------- */
 const updateDiscountType = async (id, name) => {
  try {
    const res = await fetch(`${DISCOUNT_ENDPOINT}/${id}`, {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify({ name }),
    });

    const json = await res.json();

    const item =
      json?.data && typeof json.data === "object"
        ? json.data
        : json;

    setDiscountTypes((prev) =>
      prev.map((d) =>
        d.id === id ? normalizeDiscount(item) : d
      )
    );
  } catch (err) {
    console.error("Update failed", err);
  }
};

  /* ---------- TOGGLE STATUS ---------- */
  const toggleStatus = async (id) => {
    const current = discountTypes.find((d) => d.id === id);
    if (!current) return;

    const newStatus = current.status === "Active" ? "Inactive" : "Active";

    try {
      await fetch(`${DISCOUNT_ENDPOINT}/${id}/status`, {
        method: "PATCH",
        headers: getHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });

      setDiscountTypes((prev) =>
        prev.map((d) =>
          d.id === id ? { ...d, status: newStatus } : d
        )
      );
    } catch (err) {
      console.error("Status toggle failed", err);
    }
  };

  /* ---------- DELETE ---------- */
 const deleteDiscountType = async (id) => {
  try {
    const res = await fetch(`${DISCOUNT_ENDPOINT}/${id}`, {
      method: "DELETE",
      headers: getHeaders(),
      body: JSON.stringify({
        delete_audit_id: id, 
      }),
    });

    const json = await res.json();
    console.log("Delete response:", json);

    // ✅ Update UI
    setDiscountTypes((prev) => prev.filter((d) => d.id !== id));
  } catch (err) {
    console.error("Delete failed", err);
  }
};

  /* ---------- INITIAL LOAD ---------- */
  useEffect(() => {
    fetchDiscountTypes();
  }, [fetchDiscountTypes]);

  return (
    <DiscountTypeContext.Provider
      value={{
        discountTypes,
        loading,
        error,
        fetchDiscountTypes,
        addDiscountType,
        updateDiscountType,
        toggleStatus,
        deleteDiscountType,
      }}
    >
      {children}
    </DiscountTypeContext.Provider>
  );
};

export const useDiscountType = () => {
  const ctx = useContext(DiscountTypeContext);
  if (!ctx) {
    throw new Error(
      "useDiscountType must be used inside DiscountTypeProvider"
    );
  }
  return ctx;
};
